import dynamic from 'next/dynamic';
import styled from 'styled-components';
import useSWR from 'swr';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import { CloseX } from 'shared/icons';
import { Input } from 'shared/components';
import { RecentSearchesCache } from 'search/utils/RecentSearchesCache';
import { variableFontRegular } from 'styles/theme';
import { sendTrackingEvent } from 'tracking/utils';
import { setCurrentSearchTerm } from 'store/actions/searchActions';
import { showOnMobileAndTabletOnly } from 'styles/utils/responsiveness';
import { useDebouncedValue, useFocus, useKeyDown, useConstructorClient } from 'shared/hooks';
import { useStoreDispatch } from 'store/hooks/storeHooks';

const SearchResults = dynamic(() => import('./SearchResults'));
const DefaultSearchView = dynamic(() => import('./DefaultSearchView'), { ssr: false });
interface ISearchBox {
  closeSearch?: () => void;
}

const fetchSearchResults = (ConstructorClient, searchQuery) =>
  ConstructorClient.autocomplete.getAutocompleteResults(searchQuery, {
    resultsPerSection: {
      Products: 25,
    },
  });

export const SearchBox = ({ closeSearch }: ISearchBox) => {
  const router = useRouter();
  const storeDispatch = useStoreDispatch();
  const focusRef = useFocus();
  const ConstructorClient = useConstructorClient();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebouncedValue(searchTerm, 500);
  const { data, error, isValidating } = useSWR(
    () => debouncedSearch || null,
    (searchQuery) => fetchSearchResults(ConstructorClient, searchQuery),
  );
  const searchResults = data?.sections?.Products || [];
  const totalNumResults = searchResults.length;
  const noResults = totalNumResults === 0;
  const resultId = data?.result_id;

  const handleChange = ({ target: { value } }) => {
    setSearchTerm(value);
    storeDispatch(setCurrentSearchTerm(value));
  };

  const handleClear = () => {
    setSearchTerm('');
    storeDispatch(setCurrentSearchTerm('TERM_UNKNOWN'));
  };

  const handleFocus = () => {
    ConstructorClient?.tracker?.trackInputFocus();
    sendTrackingEvent('SEARCH_BAR_TAP');
  };

  const handleSubmit = () => {
    const newEntry = JSON.stringify({ text: searchTerm, link: `/search?query=${searchTerm}` });
    RecentSearchesCache.add(newEntry);

    ConstructorClient?.tracker?.trackSearchSubmit(searchTerm, { original_query: searchTerm });
    sendTrackingEvent('SEARCH_BAR_SEARCH_TAP', {
      search_string: searchTerm,
      search_results: searchResults?.map((result) => result?.data?.slug)?.join(','),
      result_id: data?.result_id,
    });

    storeDispatch(setCurrentSearchTerm(searchTerm));
    void router.push(`/search?query=${searchTerm}`);
  };

  useKeyDown('Enter', handleSubmit);

  useEffect(() => {
    if (searchTerm) {
      ConstructorClient?.tracker?.trackSearchResultsLoaded(searchTerm, {
        num_results: totalNumResults,
        item_ids: searchResults.map((res) => res.data?.id),
      });
    }
  }, [resultId]);

  return (
    <>
      <SearchBox.SearchWrapper>
        <SearchBox.InputWrapper>
          <SearchBox.Input
            autoComplete="off"
            autoFocus
            data-qa="search_input"
            hasFloatingLabel={false}
            label="Type to search"
            name="search"
            onChange={handleChange}
            onFocus={handleFocus}
            ref={focusRef}
            spellCheck="false"
            value={searchTerm}
          />
          {searchTerm && (
            <SearchBox.Clear aria-label="Clear" onClick={handleClear} data-qa="clear_search_button">
              <CloseX title="Clear" />
            </SearchBox.Clear>
          )}
        </SearchBox.InputWrapper>
        <SearchBox.Close type="button" onClick={closeSearch}>
          Cancel
        </SearchBox.Close>
      </SearchBox.SearchWrapper>
      {searchTerm && (noResults || searchResults.length > 0 || isValidating) ? (
        <SearchResults
          error={error}
          isLoading={isValidating}
          noResults={noResults}
          resultId={resultId}
          results={searchResults}
          searchTerm={searchTerm}
        />
      ) : (
        <DefaultSearchView />
      )}
    </>
  );
};

SearchBox.SearchWrapper = styled.div`
  display: flex;
  padding: 12px 0 0 16px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    padding: 0;
    border-bottom: 1px solid ${({ theme }) => theme.navigation.searchBorderColor};
  }
`;

SearchBox.InputWrapper = styled.div`
  position: relative;
  width: 100%;
  border: ${({ theme }) => theme.borders.black};

  ${Input.Content} {
    margin-top: 0;
    margin-bottom: 0;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    border: none;
    height: 36px;
  }
`;

SearchBox.Clear = styled.button`
  ${showOnMobileAndTabletOnly`
    position: absolute;
    top: 50%;
    right: 0;
    transform: translateY(-50%);
    padding: 6px 12px;
  `}
`;

SearchBox.Input = styled(Input)`
  input {
    border: none;
    margin-bottom: 0;
    padding: 6px 0 6px 10px;
    width: 90%;
    line-height: 16px;

    &:focus {
      margin-bottom: 0;
    }
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    input {
      border: none;
      padding: 13px 16px 9px;
      width: 100%;
      line-height: 14px;
    }
  }
`;

SearchBox.Close = styled.button`
  ${showOnMobileAndTabletOnly`
    ${variableFontRegular({ size: '9.5px', height: '12px' })};
    padding: 10px 16px;

    :hover {
      text-decoration: underline;
    }
  `}
`;
